<template>
  <div id="app" class="webapp-container">
    <!-- Loading Screen -->
    <div v-if="isLoading" class="loading-screen">
      <div class="loading-spinner"></div>
      <p>Загрузка...</p>
    </div>
    
    <!-- Main Content -->
    <div v-else id="main-content" class="main-content">
      <!-- Web Header (для десктопа) -->
      <WebHeader v-if="isDesktop" />
      
      <!-- Компактная шапка для телефонов и планшетов -->
      <header v-else id="app-header" class="app-header">
        <div class="header-side header-side--left">
          <button 
            v-if="showBackButton" 
            @click="handleBack" 
            class="btn-header-icon"
            type="button"
            aria-label="Вернуться назад"
          >
            <i class="bi bi-chevron-left"></i>
          </button>
        </div>
        <button class="mobile-brand" type="button" aria-label="Открыть меню" @click="router.push('/menu')">
          <img src="/logo.svg" alt="Maxi Rolls" class="app-logo" onerror="this.onerror=null; this.src='/logo.jpg';">
        </button>
        <div class="header-side header-side--right">
          <button 
            v-if="showSearchButton" 
            @click="goToSearch" 
            class="btn-header-icon"
            type="button"
            aria-label="Поиск по меню"
          >
            <i class="bi bi-search"></i>
          </button>
          <button 
            v-if="showFavoritesButton" 
            @click="goToFavorites" 
            class="btn-header-icon"
            type="button"
            aria-label="Избранное"
          >
            <i class="bi bi-heart"></i>
          </button>
        </div>
      </header>
      
      <!-- Router View with Error Boundary -->
      <ErrorBoundary>
        <router-view v-slot="{ Component }">
          <transition name="page" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </ErrorBoundary>
      
      <!-- Bottom Navigation (только для мобильных) -->
      <BottomNav v-if="showBottomNav && !isDesktop" />
    </div>
    
    <!-- Toast Container -->
    <ToastContainer />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTelegramWebApp } from './composables/useTelegramWebApp'
import BottomNav from './components/BottomNav.vue'
import ToastContainer from './components/ToastContainer.vue'
import WebHeader from './components/WebHeader.vue'
import ErrorBoundary from './components/ErrorBoundary.vue'

const route = useRoute()
const router = useRouter()
const isLoading = ref(true)
const windowWidth = ref(window.innerWidth)
const { initTelegramWebApp } = useTelegramWebApp()

// Отслеживаем изменение размера окна
const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth
}

// Определяем, десктоп ли это
const isDesktop = computed(() => {
  return windowWidth.value >= 1024
})

// Определяем, показывать ли нижнюю навигацию
const showBottomNav = computed(() => {
  const noNavRoutes = ['/auth', '/city', '/checkout']
  return !noNavRoutes.includes(route.path)
})

// Определяем, показывать ли кнопку назад
const showBackButton = computed(() => {
  return route.meta?.showBackButton ?? false
})

// Определяем, показывать ли кнопку поиска
const showSearchButton = computed(() => {
  return route.meta?.showSearchButton ?? false
})

// Определяем, показывать ли кнопку избранного
const showFavoritesButton = computed(() => {
  return route.meta?.showFavoritesButton ?? false
})

const handleBack = () => {
  const from = route.query.from
  const categoryId = route.query.categoryId
  
  // Умная навигация на основе query параметров
  if (from === 'category' && categoryId) {
    router.push(`/category/${categoryId}`)
  } else if (from === 'search') {
    router.push('/search')
  } else if (from === 'favorites') {
    router.push('/favorites')
  } else if (window.history.length > 1) {
    router.back()
  } else {
    router.push('/menu')
  }
}

const goToSearch = () => {
  router.push('/search')
}

const goToFavorites = () => {
  router.push('/favorites')
}

onMounted(async () => {
  window.addEventListener('resize', updateWindowWidth)
  try {
    await initTelegramWebApp()
  } finally {
    isLoading.value = false
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', updateWindowWidth)
})
</script>

<style scoped>
.webapp-container {
  min-height: 100vh;
  min-height: 100dvh;
  background: var(--bg);
}

.loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  height: 100dvh;
  background: var(--bg);
}

.loading-spinner {
  width: 42px;
  height: 42px;
  border: 3px solid var(--primary-soft);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.main-content {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-bottom: calc(76px + env(safe-area-inset-bottom));
}

.app-header {
  min-height: calc(60px + env(safe-area-inset-top));
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(8px + env(safe-area-inset-top)) 14px 8px;
  background: rgba(255, 255, 255, 0.94);
  border-bottom: 1px solid var(--border);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  position: sticky;
  top: 0;
  z-index: 900;
}

.app-logo {
  display: block;
  width: auto;
  max-width: 132px;
  height: 32px;
  object-fit: contain;
}

.mobile-brand {
  position: absolute;
  left: 50%;
  top: calc(50% + env(safe-area-inset-top) / 2);
  transform: translate(-50%, -50%);
  padding: 4px 8px;
  border: 0;
  background: transparent;
}

.header-side {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 48px;
}

.header-side--right {
  justify-content: flex-end;
  margin-left: auto;
}

@media (min-width: 1024px) {
  .main-content {
    width: 100%;
    padding-bottom: 0;
  }
}

.btn-header-icon {
  width: 44px;
  height: 44px;
  background: var(--surface-muted);
  border: 1px solid transparent;
  border-radius: 14px;
  cursor: pointer;
  color: var(--text);
  font-size: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
}

.btn-header-icon:active {
  transform: scale(0.96);
  background: var(--primary-soft);
  color: var(--primary-strong);
}

.page-enter-active {
  transition: all 0.3s ease-out;
}

.page-leave-active {
  transition: all 0.2s ease-in;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(5px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-3px);
}

@media (prefers-reduced-motion: reduce) {
  .page-enter-active,
  .page-leave-active {
    transition: none;
  }
}

@media (max-width: 390px) {
  .app-logo {
    width: 106px;
    height: 28px;
  }
}
</style>

<style src="./assets/css/storefront-refresh.css"></style>

